import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AlertService } from '../../commons/services/alert.service';
import { AppState } from '../reducers';

import * as CouponsActions from '../actions/coupons.actions';
import { getSelectionDialogId, getCouponDialogId, getCouponsTableState } from '../selectors/coupons.selectors';
import { LaravelCouponsService } from '../../commons/services/laravel/laravel-coupon.service';
import { NewCouponsComponent } from '../../modules/home/modules/coupons/components/new-coupons/new-coupons.component';
import { DialogComponent } from '../../modules/shared/components/dialog/dialog.component';

@Injectable()
export class CouponsEffects {

  error$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CouponsActions.deleteCouponFailed,
          CouponsActions.saveCouponFailed
        ),
        tap(({ error }) => {
          if (error) {
            this.alertService.showErrorMessage('Errore', error);
          }
        })
      ),
    { dispatch: false }
  );

  loadCoupons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CouponsActions.loadCoupons),
      switchMap(({ page, perPage, order, direction, filters, includes }) => {
        return this.couponsService
          .list(page, perPage, order, direction, filters, includes)
          .pipe(
            map((result) =>
              CouponsActions.loadCouponsCompleted({
                  coupons: result.data,
                  currentPage: page,
                  total: result.total,
                  perPage,
                  order,
                  direction,
                  filters,
                  includes
              })
          ),
            catchError((error) =>
              of(CouponsActions.loadCouponsFailed({ error }))
            )
          );
      })
    )
  );

  changePage = createEffect(() =>
    this.actions$.pipe(
      ofType(CouponsActions.changePage),
      withLatestFrom(this.store$.select(getCouponsTableState)),
      map(
        ([
          { page, size },
          { total, currentPage, perPage, direction, order, filters, includes },
        ]) =>
          CouponsActions.loadCoupons({
            page: page,
            perPage: size,
            order,
            direction,
            filters,
            includes,
          })
      )
    )
  );

  changeSort = createEffect(() =>
    this.actions$.pipe(
      ofType(CouponsActions.changeSort),
      withLatestFrom(this.store$.select(getCouponsTableState)),
      map(
        ([
          action,
          { total, currentPage, perPage, direction, order, filters, includes },
        ]) =>
          CouponsActions.loadCoupons({
            page: currentPage,
            perPage: perPage,
            order: action.order,
            direction: action.direction,
            filters,
            includes,
          })
      )
    )
  );

  changeFilters = createEffect(() =>
    this.actions$.pipe(
      ofType(CouponsActions.changeFilters),
      withLatestFrom(this.store$.select(getCouponsTableState)),
      map(
        ([
          { filters },
          { total, currentPage, perPage, direction, order, includes },
        ]) =>
          CouponsActions.loadCoupons({
            page: currentPage,
            perPage: perPage,
            order,
            direction,
            filters,
            includes,
          })
      )
    )
  );

  editCoupon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CouponsActions.editCoupon),
      map(({ coupon }) => {
        let dialogRef = this.dialog.open(NewCouponsComponent, {
          data: {
            coupon,
          },
        });
        return CouponsActions.couponDialogOpened({
          dialogId: dialogRef.id,
        });
      })
    )
  );

  saveCoupon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CouponsActions.saveCoupon),
      mergeMap(({ coupon }) =>
        this.couponsService.upsert(coupon.toDTO()).pipe(
          map((result) =>
            CouponsActions.saveCouponCompleted({ coupon: result })
          ),
          // catchError((error) =>
          //   of(CouponsActions.saveCouponFailed({ error }))
          // )
          tap((action) => {
            if (action.type === CouponsActions.saveCouponCompleted.type) {
              this.alertService.showConfirmMessage(`Coupon ${action.coupon.code} salvato con successo`);
              this.store$.dispatch(CouponsActions.closeCouponsDialog());
            } else {
              this.store$.dispatch(CouponsActions.saveCouponFailed({ error: Error }));
            }
          }),
        )
      )
    )
  );

  // onSaveCompleted$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(CouponsActions.saveCouponCompleted),
  //     map((action) => action.coupon),
  //     tap((coupon) =>
  //       this.alertService.showConfirmMessage(
  //         `Coupon ${coupon.code} salvato con successo`
  //       )
  //     ),
  //     tap(() => CouponsActions.closeCouponsDialog())
  //   ), { dispatch: false }
  // );

  deleteCoupon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CouponsActions.deleteCoupon),
      switchMap(({ coupon }) =>
        this.alertService
          .showConfirmDialog(
            'Conferma eliminazione',
            `Sei sicuro di voler eliminare il coupon ${coupon.code}?`
          )
          .pipe(
            mergeMap((confirm) => {
              return confirm
                ? this.couponsService.delete(coupon.id).pipe(
                    map(() =>
                      CouponsActions.deleteCouponCompleted({
                        coupon,
                      })
                    ),
                    catchError((error) =>
                      of(CouponsActions.deleteCouponFailed({ error }))
                    )
                  )
                : of(CouponsActions.deleteCouponCancelled());
            })
          )
      )
    )
  );

  onDeleteCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CouponsActions.deleteCouponCompleted),
      tap(({ coupon }) =>
        this.alertService.showConfirmMessage(
          `Coupon ${coupon.code} eliminato con successo`
        )
      ),
      map(() => CouponsActions.closeCouponsDialog())
    )
  );

  // closeDialog = createEffect(
  //   () =>
  //     this.actions$.pipe(
  //       ofType(CouponsActions.closeCouponsDialog),
  //       withLatestFrom(this.store$.select(getCouponDialogId)),
  //       tap(([_, dialogId]) => {
  //         if (dialogId) {
  //           this.dialog.getDialogById(dialogId)?.close();
  //         }
  //       })
  //     ),
  //   { dispatch: false }
  // );

  reloadAfterSave = createEffect(() =>
    this.actions$.pipe(
      ofType(CouponsActions.saveCouponCompleted),
      withLatestFrom(this.store$.select(getCouponsTableState)),
      map(
        ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
          CouponsActions.loadCoupons({
            page: currentPage,
            perPage,
            order,
            direction,
            filters,
            includes,
          })
      )
    )
  );

  reloadAfterDelete = createEffect(() =>
    this.actions$.pipe(
      ofType(CouponsActions.deleteCouponCompleted),
      withLatestFrom(this.store$.select(getCouponsTableState)),
      map(
        ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
          CouponsActions.loadCoupons({
            page: currentPage,
            perPage,
            order,
            direction,
            filters,
            includes,
          })
      )
    )
  );

  closeSelectionDialog = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CouponsActions.closeSelectionDialog),
        withLatestFrom(this.store$.select(getSelectionDialogId)),
        tap(([_, dialogId]) => {
          if (dialogId) {
            this.dialog.getDialogById(dialogId)?.close();
          }
        })
      ),
    { dispatch: false }
  );


  requestCloseDialog$ = createEffect(() =>
      this.actions$.pipe(
        ofType(CouponsActions.requestCloseCouponsDialog),
        switchMap(() => {
          const confirmDialogRef = this.dialog.open(DialogComponent, {
            disableClose: false
          });

          // Imposta il messaggio del dialog
          confirmDialogRef.componentInstance.title = "Chiudi";
          confirmDialogRef.componentInstance.confirmMessage =
            "Ci sono modifiche non salvate. Sei sicuro di voler chiudere?";

          return confirmDialogRef.afterClosed().pipe(
            map(result =>
              result
                ? CouponsActions.confirmCloseCouponsDialog()
                : CouponsActions.cancelCloseCouponsDialog()
            )
          );
        })
      )
    );


    confirmCloseDialog$ = createEffect(() =>
      this.actions$.pipe(
        ofType(CouponsActions.confirmCloseCouponsDialog),
        map(() => CouponsActions.closeCouponsDialog())
      ), { dispatch: false }
    );

    cancelCloseDialog$ = createEffect(() =>
      this.actions$.pipe(
        ofType(CouponsActions.cancelCloseCouponsDialog),
        map(() => {
          // Se l'utente annulla mantiene il dialog aperto
          return { type: 'NO_ACTION' };
        })
      )
    );

    closeDialog$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(CouponsActions.closeCouponsDialog),
          withLatestFrom(this.store$.select(getCouponDialogId)),
          tap(([_, dialogId]) => {
            if (dialogId) {
              this.dialog.getDialogById(dialogId).close();
            }
          })
        ),
      { dispatch: false }
    );



  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private couponsService: LaravelCouponsService,
    private dialog: MatDialog,
    private alertService: AlertService
  ) {}
}
